import { CTA, FlexItem, FlexLayout } from "@rig/rw-core-components";
import classNames from "classnames";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Redirect, useHistory, useParams } from "react-router";
import { RoutePath, TourRoutePath } from "../../static/routes";
import { Env } from "../../static/types";
import { Loader } from "../common/Loader";
import "./Tours.scss";
import {
  AOHeaderBackground,
  AccMngTourImages,
  DIHeaderBackground,
  FeaturedTourImages,
  IncomeProjTourImages,
  InvestingPrefTourImages,
  ResearchTourImages,
  TradeHKTourImages,
  TradeTourImages,
  FeaturedTourImagesFR,
  TradeHKTourImagesFR,
  TradeTourImagesFR,
  AccMngTourImagesFR,
  InvestingPrefTourImagesFR,
  IncomeProjTourImagesFR,
  ResearchTourImagesFR,
  TourFooter,
} from "./assets";
import { dataLayerPush, scrollTo } from "../../static/utils";
import { TourKind } from "../../static/enums";
import { postOnboardingStatus } from "../../api";

const baseDigId = 'di-Authenticated-pkm4S4wL1ec3';

const digTourData: Partial<Record<TourKind, [number, string]>> = {
  [TourKind.Featured]:             [10, 'featured'],
  [TourKind.Trade]:                [14, 'trade'],
  [TourKind.TradeHK]:              [14, 'trade'],
  [TourKind.AccountManagement]:    [18, 'account management'],
  [TourKind.InvestingPreferences]: [22, 'investing preferences'],
  [TourKind.Research]:             [26, 'research'],
  [TourKind.IncomeProjection]:     [30, 'income projection'],
};

type TooltipPlacement = "top" | "left" | "right" | "bottom";

enum TourRedirect {
  onboarding = "onboarding",
}

interface TooltipProps {
  title: string;
  digTitle: string;
  description: string;
  placement?: TooltipPlacement;
  showPrev?: boolean;
  isLast?: boolean;
  onNextClick: () => void;
  onPrevClick: () => void;
  isPublicTour?: boolean;
  redirectPath?: string;
  tourId: TourKind;
  step: number;
}

const Tooltip = ({
  title,
  digTitle,
  description,
  placement = "top",
  showPrev = true,
  isLast = false,
  onNextClick,
  onPrevClick,
  isPublicTour = false,
  redirectPath,
  tourId,
  step
}: TooltipProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const btn1 = useRef(null);
  const btn2 = useRef(null);
  const btn3 = useRef(null);

  const findFirstFocusable = () => {
    const btnRefs = [btn1, btn2, btn3];
    for (const ref of btnRefs) {
      if (ref.current) {
        return ref;
      }
    }
    return null;
  }

  const redirect = () =>
    history.push(
      redirectPath === TourRedirect.onboarding
        ? RoutePath.Onboarding_Education
        : RoutePath.Education
    );

  const digIndex = digTourData[tourId][0];
  const digTourName = digTourData[tourId][1];

  const btnClick = (digIndex: number, btn: string, cb: Function) => (e: MouseEvent) => {
    dataLayerPush({
      event: 'click',
      details: `onboarding education - ${digTourName} - step ${step} - ${digTitle} - ${btn}`,
      data_dig_id: `${baseDigId}-${digIndex}`
    });
    cb(e);
  };

  return (
    <div className={`education_tour__tooltip education_tour__tooltip-${placement}`}>
      <div tabIndex={0} onFocus={() => findFirstFocusable().current?.focus()} />
        <div className="education_tour__tooltip__content">
          <FlexLayout direction="vertical">
            <FlexItem>
              <p className="education_tour__tooltip__heading">{title}</p>
            </FlexItem>
            <FlexItem>
              <p className="education_tour__tooltip__description">
                <Trans>{description}</Trans>
              </p>
            </FlexItem>
          </FlexLayout>
        </div>
        <div className="education_tour__tooltip__cta-tray">
          <div className="education_tour__tooltip__content">
            <FlexLayout
              direction="horizontal"
              horizontalAlignment={isPublicTour ? "right" : "space-between"}
              verticalAlignment="middle"
            >
              {isPublicTour || (
                <FlexItem>
                  <CTA priority="tertiary" mode="light" onClick={btnClick(digIndex, 'cancel', redirect)} ref={btn1}>
                    {t("tours.tooltip.cancel")}
                  </CTA>
                </FlexItem>
              )}
              <FlexItem>
                <FlexLayout direction="horizontal" spacingBetweenItems="xs">
                  {showPrev && (
                    <FlexItem>
                      <CTA
                        priority="secondary"
                        size="small"
                        mode="light"
                        onClick={btnClick(digIndex + 2, 'previous', onPrevClick)}
                        ref={btn2}
                      >
                        {t("tours.tooltip.previous")}
                      </CTA>
                    </FlexItem>
                  )}
                  {(isPublicTour && isLast) || (
                    <FlexItem>
                      <CTA
                        priority="primary"
                        size="small"
                        mode="light"
                        onClick={isLast ? btnClick(digIndex + 3, 'complete', redirect) : btnClick(digIndex + 1, 'next', onNextClick)}
                        ref={btn3}
                      >
                        {isLast
                          ? t("tours.tooltip.complete")
                          : t("tours.tooltip.next")}
                      </CTA>
                    </FlexItem>
                  )}
                </FlexLayout>
              </FlexItem>
            </FlexLayout>
          </div>
        </div>
      <div tabIndex={0} onFocus={() => findFirstFocusable().current?.focus()} />
    </div>
  );
};

type TourInfo = {
  tooltipDescription: string;
  title: string;
  digTitle: string;
  nubbinPosition: TooltipPlacement;
  top: string;
  left: string;
  image: string;
  isAO?: boolean;
};

export const Tours = ({
  env,
  isPublicTour = false,
}: {
  env?: Env;
  isPublicTour?: boolean;
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const history = useHistory();
  const {
    tourId: tourParam,
    step: stepParam,
    redirect: redirectParam,
  } = useParams<any>();
  const step = parseInt(stepParam);
  const tourId = tourParam?.toLowerCase();
  const redirect = redirectParam?.toLowerCase();
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);

  useEffect(() => {
    setImageLoaded(false);
    if (
      (tourId === TourKind.IncomeProjection && step === 4) ||
      (tourId === TourKind.IncomeProjection && step === 5) ||
      (tourId === TourKind.AccountManagement && step === 6) ||
      (tourId === TourKind.TradeHK && step === 2) ||
      (tourId === TourKind.Trade && step === 2)
    ) {
      scrollTo(".education_tour__tooltip_container");
    } else {
      window.scrollTo(0, 0);
    }
  }, [tourId, step]);

  const TourContent: Partial<Record<TourKind, TourInfo[]>> = {
    [TourKind.Featured]: [
      {
        tooltipDescription: t("tours.featured.1.step"),
        title: t("tours.featured.1.step.header"),
        digTitle: "your home",
        nubbinPosition: "top",
        top: "31rem",
        left: "4rem",
        image:
          language === "en"
            ? FeaturedTourImages.Featured01
            : FeaturedTourImagesFR.Featured01,
      },
      {
        tooltipDescription: t("tours.featured.2.step"),
        title: t("tours.featured.2.step.header"),
        digTitle: "monitor",
        nubbinPosition: "left",
        top: "7rem",
        left: "30.5rem",
        image:
          language === "en"
            ? FeaturedTourImages.Featured02
            : FeaturedTourImagesFR.Featured02,
      },
      {
        tooltipDescription: t("tours.featured.3.step"),
        title: t("tours.featured.3.step.header"),
        digTitle: "managing funds",
        nubbinPosition: "left",
        top: "7rem",
        left: language === "en" ? "37rem" : "36rem",
        image:
          language === "en"
            ? FeaturedTourImages.Featured03
            : FeaturedTourImagesFR.Featured03,
      },
      {
        tooltipDescription: t("tours.featured.4.step"),
        title: t("tours.featured.4.step.header"),
        digTitle: "trade & transfer",
        nubbinPosition: "top",
        top: "33.5rem",
        left: language === "en" ? "36.2rem" : "35rem",
        image:
          language === "en"
            ? FeaturedTourImages.Featured04
            : FeaturedTourImagesFR.Featured04,
      },
      {
        tooltipDescription: t("tours.featured.5.step"),
        title: t("tours.featured.5.step.header"),
        digTitle: "research",
        nubbinPosition: "left",
        top: "7rem",
        left: language === "en" ? "47rem" : "48.5rem",
        image:
          language === "en"
            ? FeaturedTourImages.Featured05
            : FeaturedTourImagesFR.Featured05,
      },
      {
        tooltipDescription: t("tours.featured.6.step"),
        title: t("tours.featured.6.step.header"),
        digTitle: "explore",
        nubbinPosition: "left",
        top: "7rem",
        left: language === "en" ? "54rem" : "56rem",
        image:
          language === "en"
            ? FeaturedTourImages.Featured06
            : FeaturedTourImagesFR.Featured06,
      },
      {
        tooltipDescription: t("tours.featured.7.step"),
        title: t("tours.featured.7.step.header"),
        digTitle: "find",
        nubbinPosition: "right",
        top: "7rem",
        left: language === "en" ? "17.5rem" : "15.5rem",
        image:
          language === "en"
            ? FeaturedTourImages.Featured07
            : FeaturedTourImagesFR.Featured07,
      },
      {
        tooltipDescription: t("tours.featured.8.step"),
        title: t("tours.featured.8.step.header"),
        digTitle: "watchlist",
        nubbinPosition: "right",
        top: "6.6rem",
        left: "18rem",
        image:
          language === "en"
            ? FeaturedTourImages.Featured08
            : FeaturedTourImagesFR.Featured08,
      },
      {
        tooltipDescription: t("tours.featured.9.step"),
        title: t("tours.featured.9.step.header"),
        digTitle: "control centre",
        nubbinPosition: "right",
        top: "5.5rem",
        left: "30.5rem",
        image:
          language === "en"
            ? FeaturedTourImages.Featured09
            : FeaturedTourImagesFR.Featured09,
      },
      {
        tooltipDescription: t("tours.featured.10.step"),
        title: t("tours.featured.10.step.header"),
        digTitle: "switch profile",
        nubbinPosition: "right",
        top: "6rem",
        left: "25.5rem",
        image:
          language === "en"
            ? FeaturedTourImages.Featured10
            : FeaturedTourImagesFR.Featured10,
      },
    ],
    [TourKind.Trade]: [
      {
        tooltipDescription: t("tours.trade.1.step"),
        title: t("tours.trade.1.step.header"),
        digTitle: "trade & transfer",
        nubbinPosition: "left",
        top: language === "en" ? "6.9rem" : "7rem",
        left: language === "en" ? "37rem" : "36.5rem",
        image:
          language === "en"
            ? TradeTourImages.Trade01
            : TradeTourImagesFR.Trade01,
      },
      {
        tooltipDescription: t("tours.trade.2.step"),
        title: t("tours.trade.2.step.header"),
        digTitle: "toolbox",
        nubbinPosition: "bottom",
        top: language === "en" ? "32.6rem" : "29.6rem",
        left: "13rem",
        image:
          language === "en"
            ? TradeTourImages.Trade02
            : TradeTourImagesFR.Trade02,
      },
      {
        tooltipDescription: t("tours.trade.3.step"),
        title: t("tours.trade.3.step.header"),
        digTitle: "dive into details",
        nubbinPosition: "bottom",
        top: language === "en" ? "11.8rem" : "10rem",
        left: language === "en" ? "12rem" : "11.9rem",
        image:
          language === "en"
            ? TradeTourImages.Trade03
            : TradeTourImagesFR.Trade03,
      },
      {
        tooltipDescription: t("tours.trade.4.step"),
        title: t("tours.trade.4.step.header"),
        digTitle: "find your",
        nubbinPosition: "top",
        top: "25.7rem",
        left: "12.2rem",
        image:
          language === "en"
            ? TradeTourImages.Trade04
            : TradeTourImagesFR.Trade04,
      },
      {
        tooltipDescription: t("tours.trade.5.step"),
        title: t("tours.trade.5.step.header"),
        digTitle: "placing an order",
        nubbinPosition: "left",
        top: "11rem",
        left: "45.6rem",
        image:
          language === "en"
            ? TradeTourImages.Trade05
            : TradeTourImagesFR.Trade05,
      },
      {
        tooltipDescription: t("tours.trade.6.step"),
        title: t("tours.trade.6.step.header"),
        digTitle: "current values",
        nubbinPosition: "right",
        top: "11rem",
        left: "13rem",
        image:
          language === "en"
            ? TradeTourImages.Trade06
            : TradeTourImagesFR.Trade06,
      },
      {
        tooltipDescription: t("tours.trade.7.step"),
        title: t("tours.trade.7.step.header"),
        digTitle: "review your order",
        nubbinPosition: "left",
        top: "11rem",
        left: "45.5rem",
        image:
          language === "en"
            ? TradeTourImages.Trade07
            : TradeTourImagesFR.Trade07,
      },
      {
        tooltipDescription: t("tours.trade.8.step"),
        title: t("tours.trade.8.step.header"),
        digTitle: "order confirmation",
        nubbinPosition: "left",
        top: "11rem",
        left: "45.5rem",
        image:
          language === "en"
            ? TradeTourImages.Trade08
            : TradeTourImagesFR.Trade08,
      },
      {
        tooltipDescription: t("tours.trade.9.step"),
        title: t("tours.trade.9.step.header"),
        digTitle: "overview",
        nubbinPosition: "bottom",
        top: "23.2rem",
        left: "12.3rem",
        image:
          language === "en"
            ? TradeTourImages.Trade09
            : TradeTourImagesFR.Trade09,
      },
    ],
    [TourKind.AccountManagement]: [
      {
        tooltipDescription: t("tours.acc_mng.1.step"),
        title: t("tours.acc_mng.1.step.header"),
        digTitle: "profile",
        nubbinPosition: "right",
        top: "9rem",
        left: "30rem",
        image:
          language === "en"
            ? AccMngTourImages.AccMng01
            : AccMngTourImagesFR.AccMng01,
      },
      {
        tooltipDescription: t("tours.acc_mng.2.step"),
        title: t("tours.acc_mng.2.step.header"),
        digTitle: "investment solutions",
        nubbinPosition: "left",
        top: "17.75rem",
        left: language === "en" ? "41.5rem" : "47.6rem",
        image:
          language === "en"
            ? AccMngTourImages.AccMng02
            : AccMngTourImagesFR.AccMng02,
        isAO: true,
      },
      {
        tooltipDescription: t("tours.acc_mng.3.step"),
        title: t("tours.acc_mng.3.step.header"),
        digTitle: "learn to trade",
        nubbinPosition: "bottom",
        top: language === "en" ? "15rem" : "15.5rem",
        left: "13rem",
        image:
          language === "en"
            ? AccMngTourImages.AccMng03
            : AccMngTourImagesFR.AccMng03,
      },
      {
        tooltipDescription: t("tours.acc_mng.4.step"),
        title: t("tours.acc_mng.4.step.header"),
        digTitle: "easy doc",
        nubbinPosition: "top",
        top: language === "en" ? "31.5rem" : "32.5rem",
        left: "20.47rem",
        image:
          language === "en"
            ? AccMngTourImages.AccMng04
            : AccMngTourImagesFR.AccMng04,
      },
      {
        tooltipDescription: t("tours.acc_mng.5.step"),
        title: t("tours.acc_mng.5.step.header"),
        digTitle: "used forms",
        nubbinPosition: "bottom",
        top: "11rem",
        left: "20.3rem",
        image:
          language === "en"
            ? AccMngTourImages.AccMng05
            : AccMngTourImagesFR.AccMng05,
      },
      {
        tooltipDescription: t("tours.acc_mng.6.step"),
        title: t("tours.acc_mng.6.step.header"),
        digTitle: "beneficiary",
        nubbinPosition: "bottom",
        top: language === "en" ? "46.5rem" : "49rem",
        left: "10.9rem",
        image:
          language === "en"
            ? AccMngTourImages.AccMng06
            : AccMngTourImagesFR.AccMng06,
      },
    ],
    [TourKind.TradeHK]: [
      {
        tooltipDescription: t("tours.trade_hk.1.step"),
        title: t("tours.trade_hk.1.step.header"),
        digTitle: "trade & transfer",
        nubbinPosition: "left",
        top: language === "en" ? "6.9rem" : "7rem",
        left: language === "en" ? "37rem" : "36.5rem",
        image:
          language === "en"
            ? TradeHKTourImages.TradeHK01
            : TradeHKTourImagesFR.TradeHK01,
      },
      {
        tooltipDescription: t("tours.trade_hk.2.step"),
        title: t("tours.trade_hk.2.step.header"),
        digTitle: "toolbox",
        nubbinPosition: "bottom",
        top: language === "en" ? "34.4rem" : "32.5rem",
        left: "13rem",
        image:
          language === "en"
            ? TradeHKTourImages.TradeHK02
            : TradeHKTourImagesFR.TradeHK02,
      },
      {
        tooltipDescription: t("tours.trade_hk.3.step"),
        title: t("tours.trade_hk.3.step.header"),
        digTitle: "dive into details",
        nubbinPosition: "bottom",
        top: language === "en" ? "11.8rem" : "10rem",
        left: "12rem",
        image:
          language === "en"
            ? TradeHKTourImages.TradeHK03
            : TradeHKTourImagesFR.TradeHK03,
      },
      {
        tooltipDescription: t("tours.trade_hk.4.step"),
        title: t("tours.trade_hk.4.step.header"),
        digTitle: "find your",
        nubbinPosition: "top",
        top: "25.7rem",
        left: "12.2rem",
        image:
          language === "en"
            ? TradeHKTourImages.TradeHK04
            : TradeHKTourImagesFR.TradeHK04,
      },
      {
        tooltipDescription: t("tours.trade_hk.5.step"),
        title: t("tours.trade_hk.5.step.header"),
        digTitle: "placing an order",
        nubbinPosition: "left",
        top: "11rem",
        left: "45.6rem",
        image:
          language === "en"
            ? TradeHKTourImages.TradeHK05
            : TradeHKTourImagesFR.TradeHK05,
      },
      {
        tooltipDescription: t("tours.trade_hk.6.step"),
        title: t("tours.trade_hk.6.step.header"),
        digTitle: "current values",
        nubbinPosition: "right",
        top: "11rem",
        left: "13rem",
        image:
          language === "en"
            ? TradeHKTourImages.TradeHK06
            : TradeHKTourImagesFR.TradeHK06,
      },
      {
        tooltipDescription: t("tours.trade_hk.7.step"),
        title: t("tours.trade_hk.7.step.header"),
        digTitle: "review your order",
        nubbinPosition: "left",
        top: "11rem",
        left: "45.5rem",
        image:
          language === "en"
            ? TradeHKTourImages.TradeHK07
            : TradeHKTourImagesFR.TradeHK07,
      },
      {
        tooltipDescription: t("tours.trade_hk.8.step"),
        title: t("tours.trade_hk.8.step.header"),
        digTitle: "order confirmation",
        nubbinPosition: "left",
        top: "11rem",
        left: "45.5rem",
        image:
          language === "en"
            ? TradeHKTourImages.TradeHK08
            : TradeHKTourImagesFR.TradeHK08,
      },
      {
        tooltipDescription: t("tours.trade_hk.9.step"),
        title: t("tours.trade_hk.9.step.header"),
        digTitle: "overview",
        nubbinPosition: "bottom",
        top: "23.2rem",
        left: "12.3rem",
        image:
          language === "en"
            ? TradeHKTourImages.TradeHK09
            : TradeHKTourImagesFR.TradeHK09,
      },
    ],
    [TourKind.InvestingPreferences]: [
      {
        tooltipDescription: t("tours.investing_pref.1.step"),
        title: t("tours.investing_pref.1.step.header"),
        digTitle: "profile",
        nubbinPosition: "right",
        top: "5.4rem",
        left: "30.2rem",
        image:
          language === "en"
            ? InvestingPrefTourImages.InvestingPref01
            : InvestingPrefTourImagesFR.InvestingPref01,
      },
      {
        tooltipDescription: t("tours.investing_pref.2.step"),
        title: t("tours.investing_pref.2.step.header"),
        digTitle: "update profile",
        nubbinPosition: "top",
        top: language === "en" ? "37.5rem" : "40.5rem",
        left: "21.3rem",
        image:
          language === "en"
            ? InvestingPrefTourImages.InvestingPref02
            : InvestingPrefTourImagesFR.InvestingPref02,
      },
      {
        tooltipDescription: t("tours.investing_pref.3.step"),
        title: t("tours.investing_pref.3.step.header"),
        digTitle: "notifications",
        nubbinPosition: "bottom",
        top: language === "en" ? "13.3rem" : "8.5rem",
        left: "14.3rem",
        image:
          language === "en"
            ? InvestingPrefTourImages.InvestingPref03
            : InvestingPrefTourImagesFR.InvestingPref03,
      },
      {
        tooltipDescription: t("tours.investing_pref.4.step"),
        title: t("tours.investing_pref.4.step.header"),
        digTitle: "experience",
        nubbinPosition: "bottom",
        top: language === "en" ? "2.4rem" : "18rem",
        left: "21.3rem",
        image:
          language === "en"
            ? InvestingPrefTourImages.InvestingPref04
            : InvestingPrefTourImagesFR.InvestingPref04,
      },
      {
        tooltipDescription: t("tours.investing_pref.5.step"),
        title: t("tours.investing_pref.5.step.header"),
        digTitle: "create watchlist",
        nubbinPosition: "bottom",
        top: language === "en" ? "0.5rem" : "-0.5rem",
        left: "13.7rem",
        image:
          language === "en"
            ? InvestingPrefTourImages.InvestingPref05
            : InvestingPrefTourImagesFR.InvestingPref05,
      },
    ],
    [TourKind.IncomeProjection]: [
      {
        tooltipDescription: t("tours.income_proj.1.step"),
        title: t("tours.income_proj.1.step.header"),
        digTitle: "visualize",
        nubbinPosition: "top",
        top: "44rem",
        left: "13.6rem",
        image:
          language === "en"
            ? IncomeProjTourImages.IncomeProj01
            : IncomeProjTourImagesFR.IncomeProj01,
      },
      {
        tooltipDescription: t("tours.income_proj.2.step"),
        title: t("tours.income_proj.2.step.header"),
        digTitle: "insights",
        nubbinPosition: "bottom",
        top: language === "en" ? "26.7rem" : "25.3rem",
        left: "14.4rem",
        image:
          language === "en"
            ? IncomeProjTourImages.IncomeProj02
            : IncomeProjTourImagesFR.IncomeProj02,
      },
      {
        tooltipDescription: t("tours.income_proj.3.step"),
        title: t("tours.income_proj.3.step.header"),
        digTitle: "earning",
        nubbinPosition: "bottom",
        top: language === "en" ? "44.2rem" : "41.3rem",
        left: "14.4rem",
        image:
          language === "en"
            ? IncomeProjTourImages.IncomeProj03
            : IncomeProjTourImagesFR.IncomeProj03,
      },
      {
        tooltipDescription: t("tours.income_proj.4.step"),
        title: t("tours.income_proj.4.step.header"),
        digTitle: "symbol view",
        nubbinPosition: "bottom",
        top: "56rem",
        left: "14.4rem",
        image:
          language === "en"
            ? IncomeProjTourImages.IncomeProj04
            : IncomeProjTourImagesFR.IncomeProj04,
      },
      {
        tooltipDescription: t("tours.income_proj.5.step"),
        title: t("tours.income_proj.5.step.header"),
        digTitle: "expand view",
        nubbinPosition: "bottom",
        top: "56rem",
        left: "14.4rem",
        image:
          language === "en"
            ? IncomeProjTourImages.IncomeProj05
            : IncomeProjTourImagesFR.IncomeProj05,
      },
    ],
    [TourKind.Research]: [
      {
        tooltipDescription: t("tours.research.1.step"),
        title: t("tours.research.1.step.header"),
        digTitle: "stay informed",
        nubbinPosition: "top",
        top: "38rem",
        left: "13rem",
        image:
          language === "en"
            ? ResearchTourImages.Research01
            : ResearchTourImagesFR.Research01,
      },
      {
        tooltipDescription: t("tours.research.2.step"),
        title: t("tours.research.2.step.header"),
        digTitle: "discover",
        nubbinPosition: "left",
        top: "14.1rem",
        left: "46rem",
        image:
          language === "en"
            ? ResearchTourImages.Research02
            : ResearchTourImagesFR.Research02,
      },
      {
        tooltipDescription: t("tours.research.3.step"),
        title: t("tours.research.3.step.header"),
        digTitle: "explore",
        nubbinPosition: "bottom",
        top: language === "en" ? "12.8rem" : "11.3rem",
        left: "15.6rem",
        image:
          language === "en"
            ? ResearchTourImages.Research03
            : ResearchTourImagesFR.Research03,
      },
      {
        tooltipDescription: t("tours.research.4.step"),
        title: t("tours.research.4.step.header"),
        digTitle: "track",
        nubbinPosition: "top",
        top: language === "en" ? "43.5rem" : "44rem",
        left: "26.6rem",
        image:
          language === "en"
            ? ResearchTourImages.Research04
            : ResearchTourImagesFR.Research04,
      },
      {
        tooltipDescription: t("tours.research.5.step"),
        title: t("tours.research.5.step.header"),
        digTitle: "tools",
        nubbinPosition: "top",
        top: "39.4rem",
        left: "13.9rem",
        image:
          language === "en"
            ? ResearchTourImages.Research05
            : ResearchTourImagesFR.Research05,
      },
      {
        tooltipDescription: t("tours.research.6.step"),
        title: t("tours.research.6.step.header"),
        digTitle: "dive into details",
        nubbinPosition: "bottom",
        top: language === "en" ? "11.9rem" : "10.3rem",
        left: "11.9rem",
        image:
          language === "en"
            ? ResearchTourImages.Research06
            : ResearchTourImagesFR.Research06,
      },
    ],
  };

  useEffect(() => {
    const APITourProp = {
      [TourKind.Featured]: "featuredTour",
      [TourKind.Trade]: "learntoTrade",
      [TourKind.TradeHK]: "learntoTrade",
      [TourKind.AccountManagement]: "accountManagement",
      [TourKind.InvestingPreferences]: "investingPreferences",
      [TourKind.Research]: "research",
      [TourKind.IncomeProjection]: "incomeProjection",
    };

    if (
      !isPublicTour &&
      Object.values(TourKind).includes(tourId) &&
      TourContent[tourId] &&
      step === TourContent[tourId].length
    ) {
      postOnboardingStatus({
        status: {
          interactiveTour: {
            [APITourProp[tourId]]: true,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublicTour, tourId, step]);

  if (Object.values(TourKind).includes(tourId) && TourContent[tourId]) {
    const curTourContent = TourContent[tourId];
    const basePath = `/Tours/${tourParam}${
      redirect ? `/${redirectParam}` : ``
    }`;

    const nextPage = () =>
      !!curTourContent[step] ? history.push({ pathname: `${basePath}/${step + 1}`, state: { samePage: true }}) : null;
    const prevPage = () =>
      step > 1 ? history.push({ pathname: `${basePath}/${step - 1}`, state: { samePage: true }}) : null;

    if (step < curTourContent?.length + 1 && step > 0) {
      const curStep = curTourContent[step - 1] as TourInfo;

      return (
        <span id="tours_main">
          <div id="hidden_images">
            {Object.values(TourContent[tourId]).map(
              ({ image }: TourInfo, i) => {
                return (
                  <img
                    src={image}
                    alt={`Tour_${tourId}_${i}`}
                    key={`Tour_${tourId}_${i}`}
                  />
                );
              }
            )}
          </div>
          <div
            id="education_tour" className={curStep.isAO ? `education_tour__account_open` : `education_tour__direct_invest`}
          >
            <div id="education_tour__header"
              style={{ backgroundImage: `url(${curStep.isAO ? AOHeaderBackground : DIHeaderBackground})` }}
            />
            <div id="education_tour__container_break">
              <div id="education_tour__container"
                className={classNames({
                  "education_tour__container_loading": !imageLoaded,
                })}>
                <img
                  className={classNames("education_tour__image", {
                    "d-none": !imageLoaded,
                  })}
                  id={`Tour_${tourId}_${step}`}
                  src={curStep.image}
                  alt={`Tour_${tourId}_${step}`}
                  ref={(img) => img?.complete && setImageLoaded(true)}
                  onLoad={() => setImageLoaded(true)}
                />
                {imageLoaded ? (
                  <div
                    className="education_tour__tooltip_container"
                    style={{
                      top: curStep.top,
                      left: curStep.left,
                    }}
                  >
                    <Tooltip
                      title={curStep.title}
                      digTitle={curStep.digTitle}
                      description={curStep.tooltipDescription}
                      placement={curStep.nubbinPosition}
                      showPrev={step > 1}
                      isLast={!curTourContent[step]}
                      onNextClick={nextPage}
                      onPrevClick={prevPage}
                      isPublicTour={isPublicTour}
                      redirectPath={redirect}
                      tourId={tourId}
                      step={step}
                    />
                  </div>
                ) : (
                  <Loader className="main-loader" />
                )}
              </div>
            </div>
            <div id="education_tour__footer">
              <img
                className="education_tour__footer_image"
                src={TourFooter}
                alt="Tour Footer"
              />
            </div>
          </div>
        </span>
      );
    }
    return <Redirect to={`${basePath}/1`} />;
  }
  return (
    <Redirect
      to={`${TourRoutePath.Tours_Featured}${
        redirect ? `/${redirectParam}` : ``
      }/1`}
    />
  );
};
